import type { AIChatRequest } from "../types/requestTypes";
import type {
  AIChatSessionHistory,
  AIChatStreamEvent,
  SessionMessage,
  SessionMessagesResponse,
  SessionResponse
} from "../types/responseTypes";

type AIChatStreamHandlers = {
  onEvent: (event: AIChatStreamEvent) => void;
  signal?: AbortSignal;
};

const AI_CHAT_BASE_URL = process.env.REACT_APP_DOMAIN;
class AIChatActions {
  async sendMessageStream(
    request: AIChatRequest,
    handlers: AIChatStreamHandlers,
    accessToken?: string
  ): Promise<void> {
    const response = await fetch(`${AI_CHAT_BASE_URL}/query`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
      },
      body: JSON.stringify(request),
      signal: handlers.signal
    });

    if (!response.ok) {
      throw new Error(`AI chat stream failed with status ${response.status}`);
    }

    if (!response.body) {
      throw new Error("AI chat stream response body is empty");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      this.throwIfAborted(handlers.signal);
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;

        handlers.onEvent(this.parseStreamEvent(trimmedLine));
      }
    }

    const trailing = buffer.trim();
    if (trailing) {
      handlers.onEvent(this.parseStreamEvent(trailing));
    }
  }

  async getChatSessionsHistory(
    accessToken: string
  ): Promise<AIChatSessionHistory[]> {
    const response = await fetch(`${AI_CHAT_BASE_URL}/sessions`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`AI chat sessions failed with status ${response.status}`);
    }

    const payload = await response.json();
    const data = this.extractSessionsArray(payload);

    return data.map((session) => ({
      sessionId: session.id_session,
      title: session.title,
      updatedAt: session.updated_at
    }));
  }

  async getSessionMessages(
    sessionId: string,
    accessToken: string,
    options?: { before?: string | null; limit?: number }
  ): Promise<SessionMessagesResponse> {
    const params = new URLSearchParams();
    if (options?.limit) params.set("limit", String(options.limit));
    if (options?.before) params.set("before", options.before);

    const qs = params.toString();
    const url = `${AI_CHAT_BASE_URL}/sessions/${sessionId}/messages${qs ? `?${qs}` : ""}`;

    const response = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (!response.ok) {
      throw new Error(
        `Failed to load session messages (status ${response.status})`
      );
    }

    const data = await response.json();
    const messagesRaw: SessionMessage[] = Array.isArray(data?.messages)
      ? (data.messages as SessionMessage[])
      : Array.isArray(data)
        ? (data as SessionMessage[])
        : [];

    return {
      messages: messagesRaw,
      has_more: typeof data?.has_more === "boolean" ? data.has_more : false,
      next_message:
        typeof data?.next_message === "string" ? data.next_message : null
    };
  }

  private throwIfAborted(signal?: AbortSignal): void {
    if (signal?.aborted) {
      throw new Error("Request aborted");
    }
  }

  private parseStreamEvent(line: string): AIChatStreamEvent {
    try {
      return JSON.parse(line) as AIChatStreamEvent;
    } catch {
      throw new Error("Invalid stream event received from AI chat API");
    }
  }

  private extractSessionsArray(payload: unknown): SessionResponse[] {
    if (Array.isArray(payload)) {
      return payload as SessionResponse[];
    }

    if (payload && typeof payload === "object") {
      const record = payload as Record<string, unknown>;
      const wrapped =
        record.sessions ?? record.content ?? record.items ?? record.data;

      if (Array.isArray(wrapped)) {
        return wrapped as SessionResponse[];
      }
    }

    throw new Error("AI chat sessions response has unexpected format");
  }
}

export const aiChatActions = new AIChatActions();
